import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  App,
  Alert,
  Badge,
  Button,
  Card,
  Descriptions,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TabsProps } from 'antd';
import {
  ArrowLeftOutlined,
  CloudOutlined,
  CloudSyncOutlined,
  EnvironmentOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  MobileOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { useDeviceDetail } from '@/hooks/useDevices';

dayjs.extend(relativeTime);
dayjs.locale('es');
import { DeviceStateColors, DeviceStateLabels, DeviceStateMap } from '@/types/device';
import { QuickCommandsBar } from './QuickCommandsBar';
import { DeviceInfoTab } from './DeviceInfoTab';
import { DeviceAppsTab } from './DeviceAppsTab';
import { DeviceCommandsTab } from './DeviceCommandsTab';
import { DeviceAuditTab } from './DeviceAuditTab';
import { DeviceLocationTab } from './DeviceLocationTab';

const { Title, Text } = Typography;

export function DeviceDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const id = idParam ? Number(idParam) : null;
  const isValidId = id !== null && !isNaN(id) && id > 0;

  const { data: device, isLoading, error, refetch } = useDeviceDetail(isValidId ? id : null);

  const [activeTab, setActiveTab] = useState('info');

  if (!isValidId) {
    return <Alert message="ID de dispositivo invalido" type="error" showIcon />;
  }

  if (isLoading) {
    return (
      <div>
        <Card><Skeleton active /></Card>
        <Card style={{ marginTop: 16 }}><Skeleton active /></Card>
      </div>
    );
  }

  if (error || !device) {
    return (
      <Alert
        message="Error al cargar el dispositivo"
        description={error instanceof Error ? error.message : 'El dispositivo no existe o no tenes permisos para verlo.'}
        type="error"
        showIcon
        action={<Button onClick={() => navigate('/devices')}>Volver</Button>}
      />
    );
  }

  const stateKey = DeviceStateMap[device.currentState];
  const stateLabel = DeviceStateLabels[stateKey];
  const stateColor = DeviceStateColors[stateKey];

  const tabs: TabsProps['items'] = [
    {
      key: 'info',
      label: <Space><InfoCircleOutlined />Informacion</Space>,
      children: <DeviceInfoTab device={device} />,
    },
    {
      key: 'apps',
      label: (
        <Space>
          <AppstoreOutlined />
          Apps
          {device.installedApps.length > 0 && (
            <Badge count={device.installedApps.length} style={{ backgroundColor: 'var(--color-primary)' }} />
          )}
        </Space>
      ),
      children: <DeviceAppsTab device={device} />,
    },
    {
      key: 'commands',
      label: (
        <Space>
          <ThunderboltOutlined />
          Comandos
          {device.pendingCommandsCount > 0 && <Badge count={device.pendingCommandsCount} />}
        </Space>
      ),
      children: <DeviceCommandsTab deviceId={device.id} />,
    },
    {
      key: 'audit',
      label: <Space><HistoryOutlined />Audit</Space>,
      children: <DeviceAuditTab deviceId={device.id} />,
    },
    {
      key: 'location',
      label: <Space><EnvironmentOutlined />Ubicacion</Space>,
      children: <DeviceLocationTab device={device} />,
    },
  ];

  return (
    <div>
      {/* Volver */}
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/devices')}
        style={{ marginBottom: 12, paddingLeft: 0 }}
      >
        Volver a la lista
      </Button>

      {/* Header con info principal */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <Space align="start" size={16}>
            <div style={{
              width: 56, height: 56, borderRadius: 12,
              background: 'var(--color-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <MobileOutlined style={{ fontSize: 28, color: '#fff' }} />
            </div>
            <Space direction="vertical" size={4}>
              <Title level={3} style={{ margin: 0 }}>
                {device.assignedToName || <Text type="secondary" italic>Dispositivo sin asignar</Text>}
              </Title>
              <Space wrap>
                <Tag color={stateColor}>{stateLabel}</Tag>
                {device.brand && device.model && <Tag>{device.brand} {device.model}</Tag>}
                {device.androidVersion && <Tag color="green">Android {device.androidVersion}</Tag>}
                {device.profileName && <Tag color="blue">{device.profileName}</Tag>}
              </Space>
              <Space wrap split={<Text type="secondary">·</Text>}>
                <Text type="secondary">
                  <CloudOutlined style={{ marginRight: 4 }} />
                  {device.enterpriseName}
                </Text>
                <Tooltip title={device.lastSyncAt ? dayjs(device.lastSyncAt).format('DD/MM/YYYY HH:mm:ss') : ''}>
                  <Text type="secondary">
                    <CloudSyncOutlined style={{ marginRight: 4 }} />
                    {device.lastSyncAt ? `Ult. sync: ${dayjs(device.lastSyncAt).fromNow()}` : 'Sin sincronizar'}
                  </Text>
                </Tooltip>
                {device.isEnrolledInGoogle ? (
                  <Tag color="success" style={{ margin: 0 }}>Enrolado en Google</Tag>
                ) : (
                  <Tag color="warning" style={{ margin: 0 }}>Pendiente de enrolar</Tag>
                )}
              </Space>
            </Space>
          </Space>
        </div>
      </Card>

      {/* Quick commands */}
      <QuickCommandsBar device={device} onCommandSent={() => {
        message.success('Comando encolado.');
        // El refetch del detalle pasa solo por el refetchInterval del hook,
        // pero igual lo forzamos para que el contador se actualice rapido
        refetch();
      }} />

      {/* Tabs */}
      <Card style={{ marginTop: 16 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} destroyInactiveTabPane />
      </Card>
    </div>
  );
}
